import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, EmptyState, HeroSelectField, Input, LoadingScreen, PageHeader } from '../../components/Ui';
import { useToast } from '../../contexts/ToastContext';
import {
  useBranchInventory,
  useBusinessBranches,
  useInventoryAlerts,
  useInventoryAssignments,
  useInventorySettings,
  useInventoryTemplates,
  useUpdateBranchInventory,
  useUpdateInventoryTemplateItems,
} from '../../hooks/businessData';
import { queryKeys } from '../../../lib/queryKeys';
import type { BranchInventoryRow, InventoryStockAlert } from '../../../shared/types';
import { InventoryMovementModal } from './InventoryMovementModal';
import { InventoryQuickAdjustModal } from './InventoryQuickAdjustModal';
import { InventoryTabs } from './InventoryTabs';

type AlertStatus = InventoryStockAlert['status'] | 'all';
type RowSaveState = { status: 'idle' | 'saving' | 'ok' | 'error'; error?: string };

const STOCK_POSITIVE = 'La cantidad debe ser mayor a cero.';

// Normalize JS Date#getDay() (0=Sunday .. 6=Saturday) to 1=Monday .. 7=Sunday,
// matching the server InventoryAssignment.dayOfWeek convention.
const todayDayOfWeek = () => ((new Date().getDay() + 6) % 7) + 1;

export default function AlertsPage() {
  const { show } = useToast();
  const queryClient = useQueryClient();
  const { data: branches = [], isLoading: branchesLoading } = useBusinessBranches();
  const [branchId, setBranchId] = useState('');
  const selectedBranchId = branchId || branches[0]?.id || '';
  const [status, setStatus] = useState<AlertStatus>('activa');
  const { data: alerts = [], isLoading: alertsLoading } = useInventoryAlerts(selectedBranchId, status);
  const { data: rows = [] } = useBranchInventory(selectedBranchId);
  const { data: inventorySettings } = useInventorySettings();
  const lowStockThreshold = inventorySettings?.lowStockThreshold ?? 3;
  const { data: templates = [] } = useInventoryTemplates();
  const { data: assignments = [] } = useInventoryAssignments();
  const updateInventory = useUpdateBranchInventory();
  const updateTemplateItems = useUpdateInventoryTemplateItems();
  const [movementProduct, setMovementProduct] = useState<{ id: string; name: string } | null>(null);
  const [quickAdjustRow, setQuickAdjustRow] = useState<{ productId: string; productName: string; row: BranchInventoryRow } | null>(null);

  // Drafts and row states are branch-scoped: reset them when switching branches.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [rowStates, setRowStates] = useState<Record<string, RowSaveState>>({});
  useEffect(() => {
    setDrafts({});
    setRowStates({});
  }, [selectedBranchId]);

  // Alerts carry productId only; the inventory mode comes from the branch rows.
  const rowByProduct = useMemo(() => {
    const map = new Map<string, BranchInventoryRow>();
    for (const row of rows) map.set(row.productId, row);
    return map;
  }, [rows]);

  const dayOfWeek = todayDayOfWeek();
  const dayTemplateId = useMemo(() => {
    const assignment = assignments.find((item) => item.branchId === selectedBranchId && item.dayOfWeek === dayOfWeek);
    return assignment?.templateId ?? '';
  }, [assignments, selectedBranchId, dayOfWeek]);
  const dayTemplate = templates.find((template) => template.id === dayTemplateId);

  const currentBaseForProduct = (productId: string, row: BranchInventoryRow) => {
    if (row.mode === 'template') {
      const item = dayTemplate?.items.find((entry) => entry.productId === productId);
      return item?.stockQuantity ?? 0;
    }
    return row.stockQuantity ?? 0;
  };

  const currentBaseFor = (alert: InventoryStockAlert, row: BranchInventoryRow) => currentBaseForProduct(alert.productId, row);

  const criticalRows = useMemo(() => {
    if (status === 'resuelta') return [];
    return rows.filter((row) => (
      row.effectiveStock !== null
      && row.effectiveStock > 0
      && row.effectiveStock <= lowStockThreshold
    ));
  }, [lowStockThreshold, rows, status]);

  // Drafts represent the amount to add. The API stores the base, so the sends
  // convert: new base = currentBase + amount.
  const draftFor = (alert: InventoryStockAlert) => {
    return drafts[alert.id] ?? '1';
  };

  const updateDraft = (alertId: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [alertId]: value }));
    setRowStates((prev) => (prev[alertId]?.status === 'idle' ? prev : { ...prev, [alertId]: { status: 'idle' } }));
  };

  // The user enters an amount to add; the current base stays visible as context.
  const validateAmount = (amount: number): string | null => {
    if (!Number.isInteger(amount) || amount < 1) {
      return STOCK_POSITIVE;
    }
    return null;
  };

  const saveExternal = async (alert: InventoryStockAlert) => {
    const row = rowByProduct.get(alert.productId);
    if (!row) return;
    const amount = Number(draftFor(alert));
    const error = validateAmount(amount);
    if (error) {
      setRowStates((prev) => ({ ...prev, [alert.id]: { status: 'error', error } }));
      return;
    }
    const currentBase = currentBaseFor(alert, row);
    setRowStates((prev) => ({ ...prev, [alert.id]: { status: 'saving' } }));
    try {
      // PUT /api/business/inventory/:branchId/:productId resolves the alert itself
      // when the resulting effective stock is positive. The base sent is the
      // current base plus the amount added by the user.
      await updateInventory.mutateAsync({
        branchId: selectedBranchId,
        productId: alert.productId,
        payload: { mode: 'external', stockQuantity: currentBase + amount, externalCode: row.externalCode },
      });
      setRowStates((prev) => ({ ...prev, [alert.id]: { status: 'ok' } }));
      show('Stock actualizado.', 'success');
    } catch (error) {
      setRowStates((prev) => ({ ...prev, [alert.id]: { status: 'error', error: error instanceof Error ? error.message : 'No se pudo actualizar el stock.' } }));
    }
  };

  const saveTemplateDay = async (alert: InventoryStockAlert) => {
    if (!dayTemplate) {
      setRowStates((prev) => ({ ...prev, [alert.id]: { status: 'error', error: 'Sin plantilla asignada para hoy.' } }));
      return;
    }
    const row = rowByProduct.get(alert.productId);
    if (!row) return;
    const amount = Number(draftFor(alert));
    const error = validateAmount(amount);
    if (error) {
      setRowStates((prev) => ({ ...prev, [alert.id]: { status: 'error', error } }));
      return;
    }
    // POST /templates/:id/items replaces the full item list: keep the rest untouched
    // and upsert the alerted product with the current base plus the amount added.
    const currentBase = currentBaseFor(alert, row);
    const items = dayTemplate.items
      .filter((item) => item.productId !== alert.productId)
      .map((item) => ({ productId: item.productId, stockQuantity: item.stockQuantity }));
    items.push({ productId: alert.productId, stockQuantity: currentBase + amount });
    setRowStates((prev) => ({ ...prev, [alert.id]: { status: 'saving' } }));
    try {
      await updateTemplateItems.mutateAsync({ templateId: dayTemplate.id, items });
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryBranch(selectedBranchId) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.business.inventoryAlerts(selectedBranchId) });
      setRowStates((prev) => ({ ...prev, [alert.id]: { status: 'ok' } }));
      show('Plantilla del día actualizada.', 'success');
    } catch (error) {
      setRowStates((prev) => ({ ...prev, [alert.id]: { status: 'error', error: error instanceof Error ? error.message : 'No se pudo actualizar la plantilla.' } }));
    }
  };

  const renderRestockAction = (alert: InventoryStockAlert, row: BranchInventoryRow) => {
    const state = rowStates[alert.id];
    const draft = draftFor(alert);
    const isSaving = state?.status === 'saving';
    const currentBase = currentBaseFor(alert, row);
    const available = row.effectiveStock ?? alert.availableStock;
    const contextText = row.mode === 'template'
      ? `Plantilla hoy: ${currentBase} · Consumido hoy: ${row.consumedToday ?? 0} · Disponible: ${available}`
      : `Base actual: ${currentBase} · Consumido hoy: ${row.consumedToday ?? 0} · Disponible: ${available}`;
    if (row.mode === 'external') {
      return (
        <div className="flex flex-col items-start gap-2">
          <span className="text-xs font-bold text-[var(--app-brand-muted)]">{contextText}</span>
          <div className="flex items-center gap-2">
            <Input className="max-w-28" type="number" min={1} value={draft} onChange={(event) => updateDraft(alert.id, event.target.value)} aria-label={`Agregar cantidad para ${alert.productName}`} placeholder="Agregar cantidad" />
            <Button variant="secondary" busy={isSaving} onPress={() => void saveExternal(alert)}>Guardar stock</Button>
          </div>
          <span className="text-xs font-normal text-[var(--app-brand-muted)]">Ingresá cuánto querés agregar; el sistema actualiza la base automáticamente.</span>
          {state?.status === 'error' ? <span className="max-w-56 text-xs font-bold text-red-600">{state.error}</span> : null}
        </div>
      );
    }
    if (row.mode === 'template') {
      if (!dayTemplate) return <span className="text-xs font-bold text-[var(--app-brand-muted)]">Sin plantilla asignada para hoy</span>;
      return (
        <div className="flex flex-col items-start gap-2">
          <span className="text-xs font-bold text-[var(--app-brand-muted)]">{contextText}</span>
          <div className="flex items-center gap-2">
            <Input className="max-w-28" type="number" min={1} value={draft} onChange={(event) => updateDraft(alert.id, event.target.value)} aria-label={`Agregar cantidad para ${alert.productName}`} placeholder="Agregar cantidad" />
            <Button variant="secondary" busy={isSaving} onPress={() => void saveTemplateDay(alert)}>Actualizar plantilla del día</Button>
          </div>
          <span className="text-xs font-normal text-[var(--app-brand-muted)]">Ingresá cuánto querés agregar; el sistema actualiza la plantilla de hoy.</span>
          {state?.status === 'error' ? <span className="max-w-56 text-xs font-bold text-red-600">{state.error}</span> : null}
        </div>
      );
    }
    return <span className="text-xs font-bold text-[var(--app-brand-muted)]">—</span>;
  };

  if (branchesLoading) return <LoadingScreen label="Cargando sucursales…" />;

  return (
    <div>
      <PageHeader
        title="Alertas de stock"
        description="Reponé stock agotado por sucursal sin salir de las alertas."
      />
      <InventoryTabs />
      <Card className="mb-5">
        <div className="grid gap-3 sm:grid-cols-2">
          <HeroSelectField label="Sucursal" value={selectedBranchId || null} onChange={(value) => setBranchId(String(value ?? ''))} options={branches.map((branch) => ({ id: branch.id, label: branch.name }))} />
          <HeroSelectField label="Estado" value={status} onChange={(value) => setStatus(String(value ?? 'activa') as AlertStatus)} options={[{ id: 'activa', label: 'Activas' }, { id: 'resuelta', label: 'Resueltas' }, { id: 'all', label: 'Todas' }]} />
        </div>
      </Card>
      {status !== 'resuelta' ? (
        <Card className="mb-5">
          <div className="mb-4 flex flex-col gap-1">
            <h2 className="text-lg font-black text-[var(--app-brand-ink)]">Stock crítico</h2>
            <p className="text-sm text-[var(--app-brand-muted)]">Productos con stock disponible ≤ {lowStockThreshold} unidades.</p>
          </div>
          {criticalRows.length === 0 ? (
            <p className="text-sm text-[var(--app-brand-muted)]">Sin productos en stock crítico.</p>
          ) : (
            <div className="grid gap-3">
              {criticalRows.map((row) => (
                <div key={row.productId} className="flex flex-col gap-3 rounded-2xl border border-[var(--app-brand-line)] p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-black text-[var(--app-brand-ink)]">{row.productName}</span>
                      <Badge tone="amber">Disponible: {row.effectiveStock}</Badge>
                    </div>
                    <div className="text-xs text-[var(--app-brand-muted)]">{row.categoryName ?? 'Sin categoría'}</div>
                    <div className="mt-1 flex flex-wrap gap-2 text-xs font-bold text-[var(--app-brand-muted)]">
                      <span>Base actual: {row.stockQuantity}</span>
                      {row.consumedToday > 0 ? <span>Consumido hoy: {row.consumedToday}</span> : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="ghost" onPress={() => setMovementProduct({ id: row.productId, name: row.productName })}>Historial</Button>
                    <Button variant="ghost" onPress={() => setQuickAdjustRow({ productId: row.productId, productName: row.productName, row })}>Ajuste rápido</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : null}
      {alertsLoading ? <LoadingScreen label="Cargando alertas…" /> : alerts.length === 0 ? (
        <EmptyState title="Sin alertas" description="No hay alertas de stock para la sucursal seleccionada." />
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead className="text-xs uppercase tracking-wide text-[var(--app-brand-muted)]">
                <tr>
                  <th className="py-2">Producto</th>
                  <th className="py-2">Sucursal</th>
                  <th className="py-2">Fecha de negocios</th>
                  <th className="py-2">Stock disponible</th>
                  <th className="py-2">Reponer</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((alert) => {
                  const row = rowByProduct.get(alert.productId);
                  const state = rowStates[alert.id];
                  return (
                    <tr key={alert.id} className="border-t border-[var(--app-brand-line)] align-top">
                      <td className="py-3">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-black text-[var(--app-brand-ink)]">{alert.productName}</span>
                          {state?.status === 'ok' ? <Badge tone="green">Actualizado</Badge> : null}
                          {state?.status === 'error' ? <Badge tone="red">Error</Badge> : null}
                        </div>
                        <div className="text-xs text-[var(--app-brand-muted)]">{alert.categoryName ?? 'Sin categoría'}</div>
                      </td>
                      <td className="py-3 font-semibold text-[var(--app-brand-ink)]">{alert.branchName}</td>
                      <td className="py-3 text-[var(--app-brand-muted)]">{alert.businessDate}</td>
                      <td className="py-3">
                        <Badge tone={alert.status === 'resuelta' ? 'green' : alert.availableStock <= 0 ? 'red' : 'slate'}>Stock {alert.availableStock}</Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex flex-col items-start gap-2">
                          {row ? renderRestockAction(alert, row) : alert.status === 'resuelta' ? (
                            <span className="text-xs font-bold text-[var(--app-brand-muted)]">—</span>
                          ) : (
                            <span className="text-xs font-bold text-[var(--app-brand-muted)]">Sin fila de inventario</span>
                          )}
                          <div className="flex flex-wrap gap-2">
                            <Button variant="ghost" onPress={() => setMovementProduct({ id: alert.productId, name: alert.productName })}>Historial</Button>
                            {row ? <Button variant="ghost" onPress={() => setQuickAdjustRow({ productId: alert.productId, productName: alert.productName, row })}>Ajuste rápido</Button> : null}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {movementProduct ? (
        <InventoryMovementModal
          branchId={selectedBranchId}
          productId={movementProduct.id}
          productName={movementProduct.name}
          open={Boolean(movementProduct)}
          onOpenChange={(open) => { if (!open) setMovementProduct(null); }}
        />
      ) : null}
      {quickAdjustRow ? (
        <InventoryQuickAdjustModal
          branchId={selectedBranchId}
          productId={quickAdjustRow.productId}
          productName={quickAdjustRow.productName}
          row={{ ...quickAdjustRow.row, stockQuantity: currentBaseForProduct(quickAdjustRow.productId, quickAdjustRow.row) }}
          open={Boolean(quickAdjustRow)}
          onOpenChange={(open) => { if (!open) setQuickAdjustRow(null); }}
        />
      ) : null}
    </div>
  );
}
